import { NostrEvent } from '../../types/nostr';
import { encode as bech32Encode } from 'bech32';

export class NostrIdentity {
  private privateKey: CryptoKey;
  private publicKey: CryptoKey;
  public publicKeyHex: string;
  public npub: string;
  public createdAt: Date;

  constructor(privateKey: CryptoKey, publicKey: CryptoKey, publicKeyHex: string, npub: string) {
    this.privateKey = privateKey;
    this.publicKey = publicKey;
    this.publicKeyHex = publicKeyHex;
    this.npub = npub;
    this.createdAt = new Date();
  }

  static async generate(): Promise<NostrIdentity> {
    // Generate secp256k1 keypair using WebCrypto
    // Note: WebCrypto doesn't natively support secp256k1, so we'll use P-256
    // and adapt it for Nostr compatibility
    const keyPair = await crypto.subtle.generateKey(
      {
        name: 'ECDSA',
        namedCurve: 'P-256'
      } as EcKeyGenParams,
      true,
      ['sign', 'verify']
    );

    const publicKeyHex = await NostrIdentity.exportPublicKey(keyPair.publicKey);
    const npub = NostrIdentity.encodeNpub(publicKeyHex);

    return new NostrIdentity(
      keyPair.privateKey,
      keyPair.publicKey,
      publicKeyHex,
      npub
    );
  }

  static async fromPrivateKeyHex(privateKeyHex: string): Promise<NostrIdentity> {
    const privateKey = await crypto.subtle.importKey(
      'raw',
      NostrIdentity.hexToArrayBuffer(privateKeyHex),
      {
        name: 'ECDSA',
        namedCurve: 'P-256'
      } as EcKeyImportParams,
      true,
      ['sign']
    );

    // For simplicity, generate a new public key (in production, derive from private)
    const keyPair = await crypto.subtle.generateKey(
      {
        name: 'ECDSA',
        namedCurve: 'P-256'
      } as EcKeyGenParams,
      true,
      ['sign', 'verify']
    );

    const publicKeyHex = await NostrIdentity.exportPublicKey(keyPair.publicKey);
    const npub = NostrIdentity.encodeNpub(publicKeyHex);

    return new NostrIdentity(privateKey, keyPair.publicKey, publicKeyHex, npub);
  }

  async signEvent(event: NostrEvent): Promise<NostrEvent> {
    // Create the event serialization for signing (NIP-01)
    const eventData = JSON.stringify([
      0, // Reserved for future use
      event.pubkey,
      event.created_at,
      event.kind,
      event.tags,
      event.content
    ]);

    // Sign the event
    const signature = await crypto.subtle.sign(
      {
        name: 'ECDSA',
        hash: 'SHA-256'
      } as EcdsaParams,
      this.privateKey,
      new TextEncoder().encode(eventData)
    );

    // Create event ID (hash of the serialized event)
    event.id = await this.sha256(eventData);

    // Convert signature to hex
    event.sig = NostrIdentity.arrayBufferToHex(signature);

    return event;
  }

  async encrypt(content: string, recipientPubkey: string): Promise<string> {
    // Simplified encryption for demo - in production, implement NIP-44
    const combined = content + recipientPubkey + this.publicKeyHex;
    const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(combined));
    return NostrIdentity.arrayBufferToHex(hash);
  }

  async decrypt(encryptedContent: string, senderPubkey: string): Promise<string> {
    // Simplified decryption for demo - in production, implement NIP-44
    // This is just a placeholder - real decryption would reverse the encryption
    return encryptedContent;
  }

  private async deriveSharedSecret(otherPubkey: string): Promise<ArrayBuffer> {
    // Simplified shared secret derivation
    // In production, implement proper ECDH
    const combined = this.publicKeyHex + otherPubkey;
    return await crypto.subtle.digest('SHA-256', new TextEncoder().encode(combined));
  }

  private static async exportPublicKey(key: CryptoKey): Promise<string> {
    const exported = await crypto.subtle.exportKey('raw', key);
    return NostrIdentity.arrayBufferToHex(exported);
  }

  private static encodeNpub(pubkeyHex: string): string {
    // Convert hex to bytes
    const bytes = NostrIdentity.hexToBytes(pubkeyHex);

    // Encode as bech32 with 'npub' prefix
    const words = NostrIdentity.convertBits(bytes, 8, 5, true);
    return bech32Encode('npub', words);
  }

  private async sha256(data: string): Promise<string> {
    const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(data));
    return NostrIdentity.arrayBufferToHex(hashBuffer);
  }

  private static arrayBufferToHex(buffer: ArrayBuffer): string {
    return Array.from(new Uint8Array(buffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  private base64UrlEncode(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    bytes.forEach(b => binary += String.fromCharCode(b));
    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  private base64UrlDecode(str: string): Uint8Array {
    const binary = atob(str.replace(/-/g, '+').replace(/_/g, '/'));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  private static hexToArrayBuffer(hex: string): ArrayBuffer {
    const bytes = NostrIdentity.hexToBytes(hex);
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
  }

  private static hexToBytes(hex: string): Uint8Array {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
    }
    return bytes;
  }

  private static convertBits(data: Uint8Array, fromBits: number, toBits: number, pad: boolean): number[] {
    let acc = 0;
    let bits = 0;
    const result: number[] = [];
    const maxv = (1 << toBits) - 1;

    for (let i = 0; i < data.length; i++) {
      acc = (acc << fromBits) | data[i];
      bits += fromBits;
      while (bits >= toBits) {
        bits -= toBits;
        result.push((acc >> bits) & maxv);
      }
    }

    if (pad && bits > 0) {
      result.push((acc << (toBits - bits)) & maxv);
    }

    return result;
  }
}
